class IntervalSearchOverlay extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.abortController = null;
    this.activeGender = 'all';
    this.requestToken = 0;
    this.isFilterIntentActive = false;
    this.recentSearchesKey = 'interval-recent-searches';
    this.recentlyViewedKey = 'interval-recently-viewed';

    this.onDocumentKeyup = this.onDocumentKeyup.bind(this);
    this.onBackdropClick = this.onBackdropClick.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onInputChange = debounce(this.onInputChange.bind(this), 300);
  }

  connectedCallback() {
    this._initialized = false;
    this.trigger = this.querySelector('[data-overlay-trigger]');
    this.trigger?.addEventListener('click', () => this.open());
  }

  _initOnce() {
    if (this._initialized) return;
    this._initialized = true;

    this.modal = this.querySelector('[data-overlay-modal]');
    this.panel = this.querySelector('[data-overlay-panel]');
    this.closeButton = this.querySelector('[data-overlay-close]');
    this.searchForm = this.querySelector('[data-search-form]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.brandPills = Array.from(this.querySelectorAll('[data-brand-pill]'));
    this.recentSearchesContainer = this.querySelector('[data-recent-searches]');
    this.brandGroup = this.querySelector('[data-brand-group]');
    this.categoryGroup = this.querySelector('[data-category-group]');
    this.brandChipsContainer = this.querySelector('[data-brand-chips]');
    this.recentlyViewedGrid = this.querySelector('[data-recently-viewed-grid]');
    this.recentlyViewedEmpty = this.querySelector('[data-recently-viewed-empty]');
    this.productsGrid = this.querySelector('[data-products-grid]');
    this.resultsMessage = this.querySelector('[data-results-message]');
    this.tabs = Array.from(this.querySelectorAll('[data-gender-tab]'));
    this.tabsContainer = this.querySelector('[data-gender-tabs]');
    this.emptyState = this.querySelector('[data-empty-state]');
    this.resultsState = this.querySelector('[data-results-state]');
    this.defaultBrandMarkup = this.brandChipsContainer ? this.brandChipsContainer.innerHTML : '';
    this.defaultBrands = this.getDefaultBrands();
    this.defaultCategories = this.getDefaultCategories();

    this.closeButton?.addEventListener('click', () => this.close());
    this.modal?.querySelector('[data-overlay-backdrop]')?.addEventListener('click', this.onBackdropClick);

    this.searchInput?.addEventListener('input', this.onInputChange);
    this.searchForm?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.addEventListener('click', this.onOverlayClick);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const gender = tab.dataset.genderTab;
        if (!gender || this.activeGender === gender) return;
        this.activeGender = gender;
        this.updateTabs();
        if (this.searchInput.value.trim()) {
          this.setSidebarSearchMode(true);
          this.fetchSearchResults(this.searchInput.value.trim());
        } else {
          this.renderRecentlyViewed();
        }
      });
    });

    this.updateTabs();
    this.setTabsVisibility(this.hasSearchIntent());
    this.renderRecentSearches();
    this.renderRecentlyViewed();
  }

  disconnectedCallback() {
    document.removeEventListener('keyup', this.onDocumentKeyup);
    this.modal?.querySelector('[data-overlay-backdrop]')?.removeEventListener('click', this.onBackdropClick);
    this.removeEventListener('click', this.onOverlayClick);
  }

  open() {
    this._initOnce();
    if (!this.modal) return;
    this.modal.hidden = false;
    this.modal.classList.remove('is-open');
    document.body.classList.add('overflow-hidden');
    document.body.classList.add('interval-search-overlay-open');
    this.closest('.section-header')?.classList.add('interval-search-overlay-open');
    document.addEventListener('keyup', this.onDocumentKeyup);
    trapFocus(this.panel, this.searchInput);
    this.setTabsVisibility(this.hasSearchIntent());
    this.searchInput?.focus();
    requestAnimationFrame(() => this.modal.classList.add('is-open'));
  }

  close(returnFocus = true) {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    this.modal.hidden = true;
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('interval-search-overlay-open');
    this.closest('.section-header')?.classList.remove('interval-search-overlay-open');
    document.removeEventListener('keyup', this.onDocumentKeyup);
    removeTrapFocus(returnFocus ? this.trigger : null);
  }

  onDocumentKeyup(event) {
    if (event.code.toUpperCase() === 'ESCAPE') {
      this.close();
    }
  }

  onBackdropClick(event) {
    event.preventDefault();
    this.close();
  }

  onOverlayClick(event) {
    const chip = event.target.closest('[data-search-chip]');
    if (!chip) return;

    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const chipType = chip.dataset.chipType || 'brand';
    const href = chip.getAttribute('href') || '';

    if (chipType === 'category') {
      if (!href) return;

      // If category link already targets search with a query, run it in-overlay.
      try {
        const url = new URL(href, window.location.origin);
        const qParam = url.searchParams.get('q');
        if (url.pathname.includes('/search') && qParam) {
          event.preventDefault();
          this.searchInput.value = qParam;
          this.isFilterIntentActive = true;
          this.setTabsVisibility(true);
          this.setSidebarSearchMode(true);
          this.showResultsState();
          this.fetchSearchResults(qParam);
          this.saveRecentSearch(qParam);
          return;
        }
      } catch (error) {
        // Fall through to direct navigation.
      }

      // For collections/pages, follow the menu link directly.
      this.close(false);
      window.location.href = href;
      return;
    }

    event.preventDefault();

    const term = chip.dataset.chipValue || (chip.textContent || '').trim();
    if (!term) return;

    this.searchInput.value = term;
    this.isFilterIntentActive = true;
    this.setTabsVisibility(true);
    this.setSidebarSearchMode(true);
    this.showResultsState();
    this.fetchSearchResults(term);
    this.saveRecentSearch(term);
  }

  onInputChange() {
    const term = this.searchInput.value.trim();

    if (!term) {
      this.setSidebarSearchMode(false);
      this.showEmptyState();
      return;
    }

    this.setTabsVisibility(this.hasSearchIntent());
    this.setSidebarSearchMode(true);
    this.showResultsState();
    this.fetchSearchResults(term);
  }

  onFormSubmit(event) {
    const term = this.searchInput.value.trim();

    if (!term) {
      event.preventDefault();
      return;
    }

    this.saveRecentSearch(term);
  }

  updateTabs() {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.genderTab === this.activeGender;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  hasSearchIntent() {
    const hasTerm = Boolean(this.searchInput?.value.trim());
    return hasTerm || this.isFilterIntentActive;
  }

  setTabsVisibility(isVisible) {
    if (!this.tabsContainer) return;

    this.tabsContainer.hidden = !isVisible;
    this.tabsContainer.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
  }

  showEmptyState() {
    this.isFilterIntentActive = false;
    this.setTabsVisibility(false);
    this.emptyState?.removeAttribute('hidden');
    this.resultsState?.setAttribute('hidden', 'hidden');
    this.productsGrid.innerHTML = '';
    this.resultsMessage.textContent = '';
    this.renderRecentlyViewed();
  }

  showResultsState() {
    this.emptyState?.setAttribute('hidden', 'hidden');
    this.resultsState?.removeAttribute('hidden');
    this.setTabsVisibility(this.hasSearchIntent());
  }

  fetchSearchResults(term) {
    const normalizedTerm = (term || '').trim();
    if (!normalizedTerm) return;

    const requestToken = ++this.requestToken;

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.resultsMessage.textContent = 'Loading...';
    const useGenderQuery = this.activeGender && this.activeGender !== 'all';
    const genderQuery = useGenderQuery ? `${normalizedTerm} tag:${this.activeGender}` : normalizedTerm;
    const naturalGenderQuery = useGenderQuery ? `${normalizedTerm} ${this.activeGender}` : normalizedTerm;

    this.requestResults(genderQuery, this.abortController.signal)
      .then((resultsMarkup) => this.hasCards(resultsMarkup) ? resultsMarkup : this.requestResults(naturalGenderQuery, this.abortController.signal))
      .then((resultsMarkup) => this.hasCards(resultsMarkup) ? resultsMarkup : this.requestResults(normalizedTerm, this.abortController.signal))
      .then((resultsMarkup) => {
        if (requestToken !== this.requestToken || this.searchInput.value.trim() !== normalizedTerm) return;
        this.renderSearchResults(resultsMarkup, normalizedTerm);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        if (requestToken !== this.requestToken) return;
        this.resultsMessage.textContent = 'Something went wrong. Please try again.';
      });
  }

  hasCards(markup) {
    return markup.includes('interval-search-overlay__card');
  }

  requestResults(query, signal) {
    const cacheKey = `${query}`.toLowerCase();
    if (this.cachedResults[cacheKey]) {
      return Promise.resolve(this.cachedResults[cacheKey]);
    }

    const url = `${routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=12&section_id=interval-search-results`;

    return fetch(url, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Search request failed (${response.status})`);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const resultsContainer =
          doc.querySelector('#shopify-section-interval-search-results [data-interval-search-results]') ||
          doc.querySelector('[data-interval-search-results]');
        const resultsMarkup = resultsContainer ? resultsContainer.innerHTML : '';
        this.cachedResults[cacheKey] = resultsMarkup;
        return resultsMarkup;
      });
  }

  renderSearchResults(markup, term = '') {
    this.productsGrid.innerHTML = markup;
    this.applyClientSideGenderFallback();
    this.updateBrandSuggestions(term);

    const visibleCards = this.productsGrid.querySelectorAll('.interval-search-overlay__card[data-visible="true"]').length;

    if (visibleCards === 0) {
      this.resultsMessage.textContent = 'No products found.';
    } else {
      this.resultsMessage.textContent = '';
    }
  }

  applyClientSideGenderFallback() {
    const cards = this.productsGrid.querySelectorAll('.interval-search-overlay__card');

    cards.forEach((card) => {
      const tags = (card.dataset.tags || '').toLowerCase();
      const title = (card.dataset.title || '').toLowerCase();
      const url = (card.dataset.url || '').toLowerCase();
      const genderText = `${tags} ${title} ${url}`;

      const womenSignals = [/\bwomen\b/, /\bwomen's\b/, /\bwomens\b/, /\bfemale\b/, /\bladies\b/];
      const menSignals = [/\bmen\b/, /\bmen's\b/, /\bmens\b/, /\bmale\b/];

      const isWomen = womenSignals.some((pattern) => pattern.test(genderText));
      const isMen = menSignals.some((pattern) => pattern.test(genderText));

      let matchesGender = true;
      if (this.activeGender === 'all') {
        matchesGender = true;
      } else if (this.activeGender === 'men') {
        if (isWomen && !isMen) {
          matchesGender = false;
        } else if (isMen) {
          matchesGender = true;
        } else {
          matchesGender = true;
        }
      } else if (this.activeGender === 'women') {
        matchesGender = isWomen;
      }

      card.hidden = !matchesGender;
      card.dataset.visible = matchesGender ? 'true' : 'false';
    });
  }

  filterBrandPills(term) {
    const normalizedTerm = term.toLowerCase();

    this.brandPills.forEach((pill) => {
      if (!normalizedTerm) {
        pill.hidden = false;
        return;
      }

      const text = (pill.textContent || '').trim().toLowerCase();
      pill.hidden = !text.includes(normalizedTerm);
    });
  }

  setSidebarSearchMode(isSearching) {
    if (this.categoryGroup) this.categoryGroup.hidden = isSearching;

    if (!isSearching && this.brandChipsContainer) {
      this.brandChipsContainer.innerHTML = this.defaultBrandMarkup;
    }
  }

  getDefaultBrands() {
    if (!this.brandChipsContainer) return [];

    return Array.from(this.brandChipsContainer.querySelectorAll('[data-brand-pill]')).map((pill) => ({
      title: (pill.dataset.chipValue || pill.textContent || '').trim(),
      href: pill.getAttribute('href') || '',
    }));
  }

  getDefaultCategories() {
    return Array.from(this.querySelectorAll('[data-chip-type="category"]')).map((chip) => ({
      title: (chip.dataset.chipValue || chip.textContent || '').trim(),
      href: chip.getAttribute('href') || '',
    }));
  }

  findCategoryByTerm(term) {
    const normalized = (term || '').trim().toLowerCase();
    if (!normalized) return null;
    return this.defaultCategories.find((category) => category.title.toLowerCase() === normalized) || null;
  }

  updateBrandSuggestions(term) {
    if (!this.brandChipsContainer) return;

    const normalizedTerm = (term || '').trim().toLowerCase();
    if (!normalizedTerm) return;

    const productCards = Array.from(
      this.productsGrid.querySelectorAll('.interval-search-overlay__card[data-visible="true"]')
    );

    const vendorSuggestions = [];
    productCards.forEach((card) => {
      const vendor = (card.dataset.vendor || '').trim();
      if (!vendor) return;
      const vendorNormalized = vendor.toLowerCase();
      if (!vendorNormalized.startsWith(normalizedTerm)) return;
      if (vendorSuggestions.some((value) => value.toLowerCase() === vendor.toLowerCase())) return;
      vendorSuggestions.push(vendor);
    });

    const menuStartsWith = this.defaultBrands
      .map((brand) => brand.title)
      .filter((title) => title.toLowerCase().startsWith(normalizedTerm));

    const menuContains = this.defaultBrands
      .map((brand) => brand.title)
      .filter((title) => title.toLowerCase().includes(normalizedTerm) && !title.toLowerCase().startsWith(normalizedTerm));

    const merged = [...menuStartsWith, ...vendorSuggestions, ...menuContains].filter(
      (value, index, list) => list.findIndex((item) => item.toLowerCase() === value.toLowerCase()) === index
    );

    const suggestions = merged.slice(0, 6);

    if (suggestions.length === 0) {
      this.brandChipsContainer.innerHTML = '';
      return;
    }

    const chipsMarkup = suggestions
      .map((suggestion) => {
        const matchingMenuBrand = this.defaultBrands.find(
          (brand) => brand.title.toLowerCase() === suggestion.toLowerCase()
        );
        const href = matchingMenuBrand?.href || `${routes.search_url}?q=${encodeURIComponent(suggestion)}&options[prefix]=last`;

        return `<a href="${href}" class="interval-search-overlay__chip" data-search-chip data-chip-type="brand" data-chip-value="${this.escapeAttribute(
          suggestion
        )}">${this.escapeHTML(suggestion)}</a>`;
      })
      .join('');

    this.brandChipsContainer.innerHTML = chipsMarkup;
  }

  escapeAttribute(value) {
    return String(value).replace(/"/g, '&quot;');
  }

  escapeHTML(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  renderRecentSearches() {
    if (!this.recentSearchesContainer) return;

    const searches = this.getStorageArray(this.recentSearchesKey);
    this.recentSearchesContainer.innerHTML = '';

    searches.forEach((term) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'interval-search-overlay__chip';
      button.textContent = term;
      button.addEventListener('click', () => {
        const matchingCategory = this.findCategoryByTerm(term);
        if (matchingCategory?.href) {
          try {
            const url = new URL(matchingCategory.href, window.location.origin);
            const qParam = url.searchParams.get('q');
            if (url.pathname.includes('/search') && qParam) {
              this.searchInput.value = qParam;
              this.isFilterIntentActive = true;
              this.setTabsVisibility(true);
              this.setSidebarSearchMode(true);
              this.showResultsState();
              this.fetchSearchResults(qParam);
              return;
            }
          } catch (error) {
            // Fall through to direct navigation.
          }

          this.close(false);
          window.location.href = matchingCategory.href;
          return;
        }

        this.searchInput.value = term;
        this.isFilterIntentActive = true;
        this.setTabsVisibility(true);
        this.setSidebarSearchMode(true);
        this.showResultsState();
        this.fetchSearchResults(term);
      });
      this.recentSearchesContainer.appendChild(button);
    });
  }

  saveRecentSearch(term) {
    const searches = this.getStorageArray(this.recentSearchesKey).filter((item) => item !== term);
    searches.unshift(term);
    localStorage.setItem(this.recentSearchesKey, JSON.stringify(searches.slice(0, 8)));
    this.renderRecentSearches();
  }

  renderRecentlyViewed() {
    if (!this.recentlyViewedGrid) return;

    const items = this.getStorageArray(this.recentlyViewedKey);

    this.recentlyViewedGrid.innerHTML = '';

    items.forEach((item) => {
      const card = document.createElement('a');
      card.className = 'interval-search-overlay__card';
      card.href = item.url || '#';
      card.innerHTML = `
        <div class="interval-search-overlay__card-image">
          ${item.image ? `<img src="${item.image}" alt="${item.title || ''}">` : ''}
        </div>
        ${item.vendor ? `<p class="interval-search-overlay__card-vendor">${item.vendor}</p>` : ''}
        ${item.title ? `<p class="interval-search-overlay__card-title">${item.title}</p>` : ''}
        ${item.price ? `<p class="interval-search-overlay__card-price">${item.price}</p>` : ''}
      `;
      this.recentlyViewedGrid.appendChild(card);
    });

    if (this.recentlyViewedEmpty) {
      this.recentlyViewedEmpty.hidden = items.length > 0;
    }
  }

  getStorageArray(key) {
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
}

if (!customElements.get('interval-search-overlay')) {
  customElements.define('interval-search-overlay', IntervalSearchOverlay);
}
